import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { FileDown } from "lucide-react";
import { api, downloadFile, errMsg } from "@/lib/api";
import { APPEAL_STATUS, fmtDate, STATUSES } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { FleetReportDialog } from "@/components/fleet/FleetReportDialog";
import { BudgetPanel } from "@/components/trustee/BudgetPanel";
import { SpendChart } from "@/components/trustee/SpendChart";
import { BudgetWarning } from "@/components/trustee/BudgetWarning";
import { useAuth } from "@/context/AuthContext";

const Tile = ({ label, value, tone = "", testId }) => (
  <div data-testid={testId} className={`rounded-2xl border bg-card p-5 ${tone}`}>
    <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="mt-3 font-display text-3xl font-extrabold">{value}</div>
  </div>
);

const Panel = ({ title, children, testId }) => (
  <section data-testid={testId} className="overflow-hidden rounded-2xl border bg-card">
    <h3 className="border-b px-5 py-4 font-bold">{title}</h3>
    <div className="divide-y">{children}</div>
  </section>
);

const dl = (path, name, params) => downloadFile(path, name, params).catch((e) => toast.error(errMsg(e)));

const Reports = () => {
  const [riders, setRiders] = useState([]);
  const [rider, setRider] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [fleetOpen, setFleetOpen] = useState(false);
  const { user } = useAuth();
  const canRiders = (user.permissions?.riders || 0) >= 1;
  useEffect(() => { if (canRiders) api.get("/riders").then((r) => setRiders(r.data)).catch(() => {}); }, [canRiders]);
  const btn = "inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground";
  return (
    <Panel title="Reports" testId="trustee-reports">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4"><span className="text-sm">Riders summary (all riders, status, due dates)</span><button onClick={() => dl("/reports/riders.pdf", "SSCBB-riders-summary.pdf")} data-testid="report-riders-btn" className={btn}><FileDown className="h-4 w-4" /> PDF</button></div>
      <div className="flex flex-wrap items-center gap-3 px-5 py-4">
        <span className="flex-1 text-sm">Assessments register</span>
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-[160px]" data-testid="report-from-input" />
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-[160px]" data-testid="report-to-input" />
        <button onClick={() => dl("/reports/assessments.pdf", "SSCBB-assessments-register.pdf", { date_from: from || undefined, date_to: to || undefined })} data-testid="report-assessments-btn" className={btn}><FileDown className="h-4 w-4" /> PDF</button>
      </div>
      {canRiders && <div className="flex flex-wrap items-center gap-3 px-5 py-4">
        <span className="flex-1 text-sm">Individual rider pack</span>
        <Select value={rider || undefined} onValueChange={setRider}><SelectTrigger className="w-[220px]" data-testid="report-rider-select"><SelectValue placeholder="Select rider" /></SelectTrigger><SelectContent>{riders.map((r) => <SelectItem key={r.id} value={r.id}>{r.last_name}, {r.first_name}</SelectItem>)}</SelectContent></Select>
        <button disabled={!rider} onClick={() => { const r = riders.find((x) => x.id === rider); dl(`/reports/rider/${rider}.pdf`, `SSCBB-rider-${r.last_name}-${r.first_name}.pdf`); }} data-testid="report-rider-btn" className={`${btn} disabled:opacity-40`}><FileDown className="h-4 w-4" /> PDF</button>
      </div>}
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4"><span className="text-sm">Fleet report (choose a period: costs per bike, faults, repairs, rides)</span><button onClick={() => setFleetOpen(true)} data-testid="report-fleet-btn" className={btn}><FileDown className="h-4 w-4" /> PDF / Excel</button></div>
      {fleetOpen && <FleetReportDialog onClose={() => setFleetOpen(false)} />}
    </Panel>
  );
};

export default function TrusteeHub() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/trustee/overview").then((r) => setD(r.data)); }, []);
  useEffect(() => {
    if (d && window.location.hash === "#budgets") setTimeout(() => document.getElementById("budgets")?.scrollIntoView({ behavior: "smooth" }), 300);
  }, [d]);
  if (!d) return <div className="h-40 animate-pulse rounded-2xl bg-card" />;
  const sc = d.status_counts;
  const chart = d.months.map((m) => ({ ...m, label: new Date(`${m.month}-01`).toLocaleDateString("en-GB", { month: "short" }) }));

  return (
    <div className="space-y-8">
      <div className="on-photo relative overflow-hidden rounded-3xl border">
        <img src="/brand/banner.png" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#111317]/80" />
        <div className="relative p-6 sm:p-10">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">Trustees</p>
          <h1 className="mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl">Trustee Hub</h1>
          <p className="mt-2 max-w-lg text-sm text-zinc-300">Governance overview of rider assessments, appeals and the fleet, with downloadable reports.</p>
        </div>
      </div>
      <BudgetWarning testid="hub-budget-warning" linkTo="/trustees#budgets" />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <Tile label="Active riders" value={d.riders_total} testId="hub-riders" />
        <Tile label="Current" value={(sc.current || 0) + (sc.due_soon || 0)} testId="hub-current" />
        <Tile label="Overdue" value={sc.overdue || 0} tone="border-l-4 border-l-destructive" testId="hub-overdue" />
        <Tile label="Referrals pending" value={d.referred.length} tone="border-l-4 border-l-amber-500" testId="hub-referred" />
        <Tile label="Open appeals" value={d.appeals.length} testId="hub-appeals" />
        <Tile label="Assessors" value={d.assessors} testId="hub-assessors" />
      </div>
      <div className="grid gap-6 lg:grid-cols-12">
        <section className="rounded-2xl border bg-card p-5 lg:col-span-8" data-testid="hub-chart">
          <h3 className="mb-4 font-bold">Assessments — last 12 months</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart}>
                <XAxis dataKey="label" stroke="#9CA3AF" fontSize={12} />
                <YAxis allowDecimals={false} stroke="#9CA3AF" fontSize={12} />
                <Tooltip contentStyle={{ background: "#1B1D21", border: "1px solid #2A2E39" }} />
                <Bar dataKey="pass" name="Successful" stackId="a" fill="#10B981" />
                <Bar dataKey="unsuccessful" name="Unsuccessful" stackId="a" fill="#DB0000" />
                <Bar dataKey="referred" name="Referred" stackId="a" fill="#F59E0B" />
                <Bar dataKey="cancelled" name="Cancelled" stackId="a" fill="#6B7280" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
        <section className="space-y-3 rounded-2xl border bg-card p-5 lg:col-span-4" data-testid="hub-fleet">
          <h3 className="font-bold">Fleet</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><div className="text-xs text-muted-foreground">Bikes</div><div className="font-mono text-xl">{d.fleet.bikes.length}</div></div>
            <div><div className="text-xs text-muted-foreground">Off road</div><div className="font-mono text-xl text-red-400">{d.fleet.off_road}</div></div>
            <div><div className="text-xs text-muted-foreground">Open faults</div><div className="font-mono text-xl text-amber-400">{d.fleet.open_faults}</div></div>
            <div><div className="text-xs text-muted-foreground">Runs (30 days)</div><div className="font-mono text-xl">{d.fleet.runs_30d}</div></div>
            <div className="col-span-2"><div className="text-xs text-muted-foreground">Miles (30 days)</div><div className="font-mono text-xl">{d.fleet.miles_30d}</div></div>
          </div>
          <Link to="/fleet" className="text-sm text-primary hover:underline">View fleet</Link>
        </section>
      </div>
      <SpendChart />
      <BudgetPanel canEdit={["admin", "trustee"].includes(user.role)} canNote={["admin", "fleet_manager"].includes(user.role)} />
      <div className="grid gap-6 lg:grid-cols-3">
        <Panel title="Referrals awaiting decision" testId="hub-referred-list">
          {d.referred.length === 0 ? <p className="px-5 py-6 text-sm text-muted-foreground">None.</p> : d.referred.map((a) => <Link key={a.id} to={`/assessments/${a.id}`} className="flex justify-between px-5 py-3 text-sm hover:bg-secondary/50"><span className="font-semibold">{a.rider_name}</span><span className="text-muted-foreground">{fmtDate(a.date)}</span></Link>)}
        </Panel>
        <Panel title="Open appeals & complaints" testId="hub-appeals-list">
          {d.appeals.length === 0 ? <p className="px-5 py-6 text-sm text-muted-foreground">None.</p> : d.appeals.map((a) => <Link key={a.id} to="/appeals" className="flex justify-between px-5 py-3 text-sm hover:bg-secondary/50"><span className="font-semibold">{a.rider_name}</span><span className="text-muted-foreground">{APPEAL_STATUS[a.status]}</span></Link>)}
        </Panel>
        <Panel title="Re-assessments due / overdue" testId="hub-upcoming">
          {d.upcoming.length === 0 ? <p className="px-5 py-6 text-sm text-muted-foreground">None.</p> : d.upcoming.map((r) => <Link key={r.id} to={`/riders/${r.id}`} className="flex items-center justify-between px-5 py-3 text-sm hover:bg-secondary/50"><span className="font-semibold">{r.first_name} {r.last_name}</span><StatusBadge status={r.status} /></Link>)}
        </Panel>
      </div>
      <p className="text-xs text-muted-foreground">Status key: {Object.values(STATUSES).map((s) => s.label).join(" · ")}</p>
      <Reports />
    </div>
  );
}
